import type { Environment } from '@sg/common';
import { detectExternalProvider, oidcUriNormalizer } from '../utils';
import type { SgApplicationConfiguration } from './types';

const PATHNAME_DETECT_REGEXP = new RegExp(`^(?:#/|#|/)(sg-lender)(?:/|\\?|().*)?$`);
const HOSTNAME_DETECT_REGEXP = new RegExp(`^lender(?:-.*)?\\.sharegain\\.com$`);

const CITI_SUBDOMAIN_SALES = 'citi-sales';
// this is for testing purposes, because we want to be able to test the real behavior
const CITI_SUBDOMAIN_TEST = 'citi-test';

const ALLOWED_HOSTNAMES = [
  `citi.sharegain.com`,
  `${CITI_SUBDOMAIN_TEST}.sharegain.com`,
  `${CITI_SUBDOMAIN_SALES}.sharegain.com`,
  'ldashboard.sharegain.com',
  'ldashtest.sharegain.com',
];

declare module '@sg/pre-app-init/src/lib/application' {
  export interface SgCustomAppBranding {
    'sg-lender': {
      hideTopBar: boolean;
    };
  }
}

export const sgLender: SgApplicationConfiguration<'sg-lender'> = {
  name: 'sg-lender',
  authType: 'oidc',
  branding: {
    css: 'sg-lender-theme',
    theme: 'light',
    backgroundImg: 'assets/sg-lender/images/lender-background.svg',
    favIcon: 'assets/images/favicon/favicon.ico',
    stickyTopNav: false,
    hideActionsMenu: true,
    showSideBarExpender: true,
    sideMenuInitialState: 'closed',
    generalErrorContainerType: 'toaster',
    disableCloseSideNav: false,
    hideTopBar: true,
    showBurgerMenu: false,
    logo: 'assets/images/white-sharegain-logo.svg',
    showAppName: true,
    navBarLogoSrc: 'assets/images/logos/sharegain_logo.svg',
    navBarHomeLink: '',
    showLogo: true,
    appName: 'Lender',
    expanderPosition: 'bottom',
    isFloatingSideBar: true,
    showUserMenu: true,
    showNotifications: true,
  },
  brandingMap: {
    [CITI_SUBDOMAIN_TEST]: {
      redirectToBranding: 'citi',
    },
    [CITI_SUBDOMAIN_SALES]: {
      redirectToBranding: 'citi',
    },
    citi: {
      appName: '',
      css: 'sg-citi-lender-theme',
      logo: 'assets/images/sharegain-logo-citi.svg',
      preLogo: 'assets/images/sharegain-logo-citi.svg',
      favIcon: 'assets/images/favicon/citi-favicon.ico',
      navBarLogoSrc: 'assets/images/citi-powered-by-sg.svg',
      stickyTopNav: false,
      hideActionsMenu: true,
      showSideBarExpender: true,
      sideMenuInitialState: 'closed',
    },
    standAlone: {
      name: 'standAlone',
      hideSideNav: true,
      hideTopBar: true,
      showSideBarExpender: true,
      sideMenuInitialState: 'closed',
      custom: {
        hideTopBar: true,
      },
    },
    /**
     * American prospect-demo branding. Activated by `?demo=american` (the
     * demo-flag util in app-bootstrap/utils.ts maps the demo to a brand name).
     * Uses the standard Sharegain logo + favicon (black & white) — no
     * prospect-specific placeholder assets.
     */
    american: {
      appName: '',
      css: 'sg-lender-theme',
      logo: 'assets/images/white-sharegain-logo.svg',
      preLogo: 'assets/images/white-sharegain-logo.svg',
      favIcon: 'assets/images/favicon/favicon.ico',
      navBarLogoSrc: 'assets/images/logos/sharegain_logo.svg',
      stickyTopNav: false,
      hideActionsMenu: true,
      showSideBarExpender: true,
      sideMenuInitialState: 'closed',
    },
    /**
     * Greek prospect-demo branding. Activated by `?demo=greek`.
     * Real customer data was provided as "Eurobank Equities Investment Firm S.A."
     * — renamed to "Greek Equities Investment Firm S.A." for the demo.
     */
    greek: {
      appName: '',
      css: 'sg-lender-theme',
      logo: 'assets/images/white-sharegain-logo.svg',
      preLogo: 'assets/images/white-sharegain-logo.svg',
      favIcon: 'assets/images/favicon/favicon.ico',
      navBarLogoSrc: 'assets/images/logos/sharegain_logo.svg',
      stickyTopNav: false,
      hideActionsMenu: true,
      showSideBarExpender: true,
      sideMenuInitialState: 'closed',
    },
    /**
     * RBC prospect-demo branding. Activated by `?demo=rbc`.
     *
     * The only demo brand carrying the prospect's OWN side-nav mark: `american`
     * and `greek` both show the plain Sharegain logo there. `rbc-powered-by-sg
     * .svg` is the `citi` lockup with the Citi mark swapped for RBC's shield —
     * same canvas, same divider, same "Powered by Sharegain" block — so the two
     * co-branded headers line up pixel for pixel.
     *
     * Theme stays `sg-lender-theme`: unlike `citi` there is no RBC colour theme,
     * and inventing one would put unreviewed brand colours on every surface. That
     * theme carries the one rule a 123px lockup needs to escape the nav's 23px
     * logo slot, scoped to this asset — see its `.app-logo img[src$=…]` block.
     *
     * CLEARANCE NOT CONFIRMED, and the asset is NOT demo-gated. `angular.json`
     * declares one `assets` list for every configuration, so unlike the rest of
     * this base — which `fileReplacements` strips from ci/prod/sales —
     * `rbc-powered-by-sg.svg` is served from every deployed build. It carries a
     * third party's registered mark, and `prospect-demo`'s own rule is not to
     * ship a prospect's logo without marketing/legal sign-off. Get that
     * confirmed, or demo-gate the asset, before this reaches a real environment.
     * The clean-bundle gate does NOT cover this: it scans JS chunks, not assets.
     */
    rbc: {
      appName: '',
      css: 'sg-lender-theme',
      logo: 'assets/images/white-sharegain-logo.svg',
      preLogo: 'assets/images/white-sharegain-logo.svg',
      favIcon: 'assets/images/favicon/favicon.ico',
      navBarLogoSrc: 'assets/images/rbc-powered-by-sg.svg',
      stickyTopNav: false,
      hideActionsMenu: true,
      showSideBarExpender: true,
      // OPEN, unlike every other lender brand. The contracted nav is 64px and a
      // co-branded lockup is 123px, so collapsed it shows the prospect's mark
      // with "Powered by Sharegain" cut off mid-word. The rep can still collapse
      // it — `showSideBarExpender` is on — this only sets what the demo opens to.
      sideMenuInitialState: 'opened',
    },
  },
  matcher: (location: Location) => {
    return (
      PATHNAME_DETECT_REGEXP.test(location.pathname) ||
      PATHNAME_DETECT_REGEXP.test(location.hash) ||
      HOSTNAME_DETECT_REGEXP.test(location.hostname) ||
      ALLOWED_HOSTNAMES.includes(location.hostname)
    );
  },
  beforeLogin: (env: Environment) => {
    const { oidc } = env;
    oidc.acr_values = 'tenant:Lender';

    const externalProvider = detectExternalProvider();
    if (externalProvider) {
      oidc.acr_values = 'tenant:Lender idp:' + externalProvider;
    }

    // by default, OIDC redirects are set under the assumption of a path app (hostname.com/SG-APPNAME)
    // when it's not the case we need to set the redirects properly.
    if (!PATHNAME_DETECT_REGEXP.test(location.pathname) && !PATHNAME_DETECT_REGEXP.test(location.hash)) {
      oidcUriNormalizer(oidc);
    }
  },
};
